import { useMutation, useQuery, UseQueryResult, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { BannerState, SessionBanner } from '@/types/banners';
import { useCallback, useEffect } from 'react';
import { getBannersStates, updateBannerState } from '@/api/accountSettings';
import { IntegratedApp } from '@/constants/apps';
import { useCurrentApp } from '@/stores/userDataStore';
export const useBannerState = (banner?: SessionBanner) => {
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const { appUrl, isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();

  const useGetBannerState = (select?: (data) => boolean) => {
    return useQuery({
      queryKey: ['bannerState', banner],
      queryFn: () =>
        getBannersStates({
          appUrl,
          appName: IntegratedApp.STUDIO_SHOPIFY,
        }).then((res) => res[banner!] || BannerState.OPEN),
      select,
      enabled: !!appUrl && isLoggedIn,
      staleTime: Infinity,
    });
  };

  const useGetBannersStates = (banners: SessionBanner[]): UseQueryResult<Record<SessionBanner, string>> => {
    let allInCache = true;
    const data = {} as Record<SessionBanner, string>;
    for (const banner of banners) {
      if (!queryClient.getQueryData(['bannerState', banner])) {
        allInCache = false;
        break;
      } else {
        data[banner] = queryClient.getQueryData(['bannerState', banner]) as string;
      }
    }
    if (allInCache) {
      queryClient.setQueryData(['bannersStates', banners], data);
    }
    const result = useQuery<Record<SessionBanner, BannerState>, Error, Record<SessionBanner, string>>({
      // will return the data from the cache if all the banners are in the cache
      queryKey: ['bannersStates', banners],
      queryFn: async () =>
        getBannersStates({
          appUrl,
          appName: IntegratedApp.STUDIO_SHOPIFY,
        }),
      enabled: !!appUrl && isLoggedIn,
      staleTime: Infinity,
    });
    useEffect(() => {
      if (result.data) {
        for (const [key, value] of Object.entries(result.data)) {
          queryClient.setQueryData(['bannerState', key], value);
        }
      }
    }, [result.data]);
    return result;
  };

  const useIsBannerHidden = (): UseQueryResult<boolean> => {
    return useGetBannerState((data) => data === BannerState.CLOSED);
  };

  const useAreBannersHidden = (banners: SessionBanner[]) => {
    const { data, isLoading: isLoadingBanners, ...rest } = useGetBannersStates(banners);
    const isLoading = isLoadingUser || isLoadingBanners;
    if (isLoading) {
      return { data: null, isLoading: true, ...rest };
    }
    if (!data) {
      return {
        data: banners.reduce(
          (acc, banner) => {
            acc[banner] = false;
            return acc;
          },
          {} as Record<SessionBanner, boolean>
        ),
        isLoading,
        ...rest,
      };
    }
    return {
      data: banners.reduce(
        (acc, banner) => {
          acc[banner] = data[banner] === BannerState.CLOSED;
          return acc;
        },
        {} as Record<SessionBanner, boolean>
      ),
      isLoading,
      ...rest,
    };
  };

  const useUpdateBannerState = (visible: boolean) => {
    return useMutation({
      mutationFn: async () => {
        queryClient.setQueryData(['bannerState', banner], visible ? BannerState.OPEN : BannerState.CLOSED); // optimistic update
        return updateBannerState({
          appUrl,
          appName: IntegratedApp.STUDIO_SHOPIFY,
          bannerState: {
            [banner!]: visible ? BannerState.OPEN : BannerState.CLOSED,
          },
        });
      },
      onSuccess: () => {
        notify('Banner state updated');
      },
      onError: () => {
        queryClient.setQueryData(['bannerState', banner], !visible ? BannerState.OPEN : BannerState.CLOSED); // revert optimistic update
        notify('Failed to update banner state');
      },
    });
  };

  const useBannersPrefetch = (banners: SessionBanner[]) => {
    const prefetchBannersStates = useCallback(() => {
      if (!appUrl || !isLoggedIn) {
        return;
      }
      queryClient
        .prefetchQuery({
          queryKey: ['bannersStates', banners],
          queryFn: () =>
            getBannersStates({
              appUrl,
              appName: IntegratedApp.STUDIO_SHOPIFY,
            }),
          staleTime: 5 * 60 * 1000,
        })
        .then(() => {
          const data = queryClient.getQueryData(['bannersStates', banners]) as Record<SessionBanner, string>;
          for (const banner of Object.keys(data)) {
            queryClient.setQueryData(['bannerState', banner], data[banner as SessionBanner]);
          }
        });
    }, [appUrl, banners, queryClient, isLoggedIn]);

    useEffect(() => {
      prefetchBannersStates();
    }, [prefetchBannersStates]);
  };
  return {
    useUpdateBannerState,
    useGetBannersStates,
    useAreBannersHidden,
    useGetBannerState,
    useIsBannerHidden,
    useBannersPrefetch,
  };
};
